import { z } from 'zod/v4';

/**
 * Path parameters of `POST /api/questionnaires/requests/:requestId/audits`.
 */
export const CreateQuestionnaireRequestAuditParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
});

export type CreateQuestionnaireRequestAuditParams = z.infer<
  typeof CreateQuestionnaireRequestAuditParamsSchema
>;

/**
 * Body of `POST /api/questionnaires/requests/:requestId/audits`.
 *
 * **No `companyId`** — the audit is created on the company the answer request is
 * about. **No `isPublic`** either, and that one is a safety property rather than
 * a convenience: a public audit is readable by every company connected to the
 * audited one, so one public audit created here would hand the subject
 * company's whole network read access to a report it never chose to publish.
 * Publishing is the subject company's decision to make, not the answering
 * party's. The route stores `false` unconditionally.
 *
 * `shareWithCompanyId` is absent too: the audit is shared with the subject
 * company and with the brand that issued the request, both derived from it.
 *
 * `workflowId` and `generatedBy` are excluded — those belong to the audit-AI
 * upload flow, which does not run through a questionnaire answer.
 */
export const CreateQuestionnaireRequestAuditBodySchema = z.object({
  auditTypeId: z.string().min(1).max(255),
  auditDate: z.string().min(1).max(64).nullable().optional(),
  nextPlannedAuditDate: z.string().min(1).max(64).nullable().optional(),
  auditedBy: z.string().min(1).max(255).nullable().optional(),
  facilityScore: z.string().min(1).max(255).nullable().optional(),
  referenceNumber: z.string().min(1).max(255).nullable().optional(),
  auditReportFileId: z.string().min(1).max(36).nullable().optional(),
  correctiveActionPlanFileId: z.string().min(1).max(36).nullable().optional(),
});

export type CreateQuestionnaireRequestAuditBody = z.infer<
  typeof CreateQuestionnaireRequestAuditBodySchema
>;

/** Enough for the answer page to link the new audit. */
export const CreateQuestionnaireRequestAuditResponseSchema = z.object({
  id: z.string(),
  companyId: z.string().nullable(),
  auditTypeId: z.string().nullable(),
});

export type CreateQuestionnaireRequestAuditResponse = z.infer<
  typeof CreateQuestionnaireRequestAuditResponseSchema
>;
